import { PeriodItemI, PeriodItemITypeEnum, PeriodsI } from '@models/periods';
import { Settings } from '@models/settings';

/**
 * Settings key triplets: [periodName, startDateKey, endDateKey]
 * Used to map settings date fields to period calculations.
 */
const SETTINGS_KEY_PAIRS: string[][] = [
  // Only LIB DST
  ['regulier', 'regulierDatumVanaf', 'regulierDatumTot'],
  ['inhaal', 'inhaalDatumVanaf', 'inhaalDatumTot'],
  ['audioBeschikbaar', 'audioBeschikbaarDatumVanaf'],
  ['pdfBeschikbaar', 'pdfBeschikbaarDatumVanaf'],
  // LIB DST & DOE DST
  ['plannenPapierDichtzetten', 'plannenPapierDichtzettenDatum', ''],
  // LIB, DOE & OCW DST
  ['afnamesBeschikbaar', 'afnamesBeschikbaarVanaf', ''],
  ['rapportagesBeschikbaar', 'rapportagesBeschikbaarVanaf', ''],
  ['schooladvies', 'schooladviesDatumVanaf', 'schooladviesDatumTot'],
  // DOE & OCW DST
  ['handmatigeInvoerDeelnemers', 'handmatigeInvoerDeelnemersDatumVanaf', ''],
  // Only DOE DST
  ['voorlopigSchooladvies', 'voorlopigSchooladviesDatumVanaf', 'voorlopigSchooladviesDatumTot'],
  ['inschrijving', 'inschrijvingDatumVanaf', ''],
  ['aanmeldenVO', 'aanmeldenVODatumVanaf', 'aanmeldenVODatumTot'],
  ['campagneJaar', 'campagneJaarDatumVanaf', 'campagneJaarDatumTot'],
];

/**
 * Create a period object from a settings date pair.
 * Pure function — no side effects, does not mutate input.
 */
export function settingsDatesToPeriod(keys: string[], settings: Settings): PeriodItemI {
  const settingsRecord = settings as unknown as Record<string, string>;

  const startDateValue = settingsRecord[keys[1]];
  const endDateValue = keys[2] !== undefined ? settingsRecord[keys[2]] : undefined;

  return datesToPeriod(startDateValue, endDateValue);
}

/**
 * Create the inplanperiode of a single toets from the toets settings.
 * Resolves the dates the same way the BFF validates a planning
 * (PlanningToegestaanVolgensHuidigeDatumSettingsValidator): the toets-specific setting wins,
 * otherwise the default toets setting, and for the end date finally regulierDatumVanaf.
 */
export function toetsInplanPeriod(settings: Settings | null | undefined, toetsBusinessKey: string): PeriodItemI {
  const toetsSettings = settings?.toetsSettings;
  const specifiek = toetsSettings?.specifiekeToetsSettings?.find((s) => s.businessKey === toetsBusinessKey);

  const startDateValue = specifiek?.datumInplanbaarVanaf || toetsSettings?.defaultDatumInplanbaarVanaf;
  const endDateValue =
    specifiek?.datumInplanbaarTot || toetsSettings?.defaultDatumInplanbaarTot || settings?.regulierDatumVanaf;

  return datesToPeriod(startDateValue, endDateValue);
}

function datesToPeriod(startDateValue: string | undefined, endDateValue: string | undefined): PeriodItemI {
  const currentUtcDate = new Date();

  let withinRange: boolean;
  let type: PeriodItemITypeEnum;

  if (startDateValue && endDateValue) {
    withinRange =
      currentUtcDate >= new Date(startDateValue) && currentUtcDate <= new Date(endDateValue);
    type = PeriodItemITypeEnum.RANGE;
  } else if (startDateValue && !endDateValue) {
    withinRange = currentUtcDate >= new Date(startDateValue);
    type = PeriodItemITypeEnum.FROM;
  } else if (!startDateValue && endDateValue) {
    withinRange = currentUtcDate <= new Date(endDateValue);
    type = PeriodItemITypeEnum.TO;
  } else {
    withinRange = true;
    type = PeriodItemITypeEnum.NONE;
  }

  return {
    withinRange,
    startDate: (typeof startDateValue === 'string' && startDateValue) ? startDateValue : null,
    endDate: (typeof endDateValue === 'string' && endDateValue) ? endDateValue : null,
    currentUtcDate: currentUtcDate.toISOString(),
    type,
  };
}

/**
 * Calculate all periods from settings.
 * Pure function — automatically derives withinRange booleans from settings dates.
 */
export function settingsToCompletePeriods(settings: Settings): PeriodsI {
  const periods = {} as PeriodsI;
  SETTINGS_KEY_PAIRS.forEach((settingKeyPair) => {
    periods[settingKeyPair[0]] = settingsDatesToPeriod(settingKeyPair, settings);
  });
  return periods;
}
